import React from 'react';
import PropTypes from 'prop-types';
import { values, compose } from 'underscore';
import { withInstrumentation } from '@bingads-webui-react/hoc-with-instrumentation';
import { CampaignType } from '@bingads-webui-campaign-react/shell-ui-common';
import { withSiteMap, siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { SummaryBarContainer } from '@bingads-webui-react/summary-bar';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import { SharedBudgetConsumer } from './helpers/shared-budget-consumer';
import { sharedBudgetItems } from './helpers';
import { SummaryBarItemsResizeGroup } from './components/summary-bar-items-resize-group';
import { getSummaryBarStyle } from './styles';
import { WithBulkUpdateRepository, WithSharedBudgetRepository } from './repository';

const SharedBudgetSummaryBarInternal = ({
  i18n,
  scope,
  siteMap,
  canEdit,
  instrumentation,
  permissions,
}) => {
  const consumeSharedBudgetRepository = SummaryBarWithBudgetCriterionResults => (
    (
      <WithSharedBudgetRepository
        WrappedComponent={SummaryBarWithBudgetCriterionResults}
        budgetId={scope.values.budgetId}
        i18n={i18n}
      />
    )
  );

  // eslint-disable-next-line react/prop-types
  const renderSharedBudgetSummaryBar = () => ({ sharedBudgetFromOData: budget }) => {
    const updateSharedBudget = patch => WithBulkUpdateRepository({
      patch,
      scope,
      edmCampaign,
      updateLevel: 'Budget',
    });

    const summaryBarActivityLogger = instrumentation.summaryBarScenario.create();

    const sharedBudgetBarItems = sharedBudgetItems({
      scope, i18n, canEdit, updateSharedBudget, summaryBarActivityLogger, budget,
    });

    const repositoryConsumer = new SharedBudgetConsumer({ scope, i18n, budget });
    const { renderSharedBudgetBreadCrumb } = repositoryConsumer.criteriaRenderer;
    const { uid, cid, aid } = scope.values;

    const backButtonProps = {
      url: `/campaign/vnext/sharedbudgets?uid=${uid}&cid=${cid}&aid=${aid}`,
      buttonText: i18n.getString(_TL_('Back to shared budgets')),
    };

    return (
      <SummaryBarContainer
        texts={{
          ariaLabel: i18n.getString(_TL_('Summary bar')),
        }}
        backButtonProps={backButtonProps}
        breadcrumb={renderSharedBudgetBreadCrumb()}
        summaryBar={<SummaryBarItemsResizeGroup summaryBarItems={sharedBudgetBarItems} scope={scope} siteMap={siteMap} />}
        showScopeBackButton={isNavigationRefreshOn(permissions)}
        i18n={i18n}
      />
    );
  };

  return compose(consumeSharedBudgetRepository, renderSharedBudgetSummaryBar)();
};

SharedBudgetSummaryBarInternal.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    campaignType: PropTypes.oneOf(values(CampaignType)).isRequired,
    budget: PropTypes.number.isRequired,
    bidStrategy: PropTypes.string.isRequired,
    locations: PropTypes.array,
    audiences: PropTypes.array,
    devices: PropTypes.array,
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: siteMapPropType.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  canEdit: PropTypes.bool,
  instrumentation: PropTypes.shape({
    summaryBarScenario: PropTypes.any,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
    }),
  }).isRequired,
};

SharedBudgetSummaryBarInternal.defaultProps = {
  canEdit: false,
};

export const SharedBudgetSummaryBar = withPermissions(withInstrumentation(withSiteMap(withDefaultStyles(
  SharedBudgetSummaryBarInternal,
  getSummaryBarStyle
))));
